import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, MessageCircle, Mail, Send, UserCheck, UserX, Loader2, Check, X as XIcon, Clock } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { chatApi } from '../../services/cloudApi';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import { getSocket } from '../../services/socketService';
import { getPlusEmailColor } from '../../utils/userDisplay';
import Username from '../Common/Username';

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

// Helper function to format time
const formatMessageTime = (timestamp, t, dateLocale) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('friendsListModal.relativeTime.now');
    if (diffMins < 60) return t('friendsListModal.relativeTime.minutesShort', { count: diffMins });
    if (diffHours < 24) return t('friendsListModal.relativeTime.hoursShort', { count: diffHours });
    if (diffDays < 7) return t('friendsListModal.relativeTime.daysShort', { count: diffDays });
    return format(date, 'd MMM', { locale: dateLocale });
  } catch (error) {
    return '';
  }
};

const FriendsListModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('chat');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { friends, reloadFriends, loadPendingFriendRequestCount, deleteFriend } = useChat();
  const { authToken } = useAuth();
  const [activeTab, setActiveTab] = useState('incoming'); // 'delete', 'incoming', 'outgoing'
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [isLoadingIncoming, setIsLoadingIncoming] = useState(false);
  const [isLoadingOutgoing, setIsLoadingOutgoing] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);

  // Load incoming requests
  const loadIncomingRequests = async () => {
    if (!authToken) return;
    setIsLoadingIncoming(true);
    try {
      const response = await chatApi.getIncomingRequests({ token: authToken });
      if (response.ok) {
        setIncomingRequests(response.requests || []);
      }
    } catch (error) {
      console.error('Load incoming requests error:', error);
    } finally {
      setIsLoadingIncoming(false);
    }
  };

  // Load outgoing requests
  const loadOutgoingRequests = async () => {
    if (!authToken) return;
    setIsLoadingOutgoing(true);
    try {
      const response = await chatApi.getOutgoingRequests({ token: authToken });
      if (response.ok) {
        setOutgoingRequests(response.requests || []);
      }
    } catch (error) {
      console.error('Load outgoing requests error:', error);
    } finally {
      setIsLoadingOutgoing(false);
    }
  };

  // Load all requests when modal opens (to show counts in tabs)
  useEffect(() => {
    if (isOpen && authToken) {
      // Always load both incoming and outgoing requests to show counts
      loadIncomingRequests();
      loadOutgoingRequests();
      // Update pending friend request count when modal opens
      if (loadPendingFriendRequestCount) {
        loadPendingFriendRequestCount();
      }
    }
  }, [isOpen, authToken, loadPendingFriendRequestCount]);

  // Reload active tab when it changes (for fresh data)
  useEffect(() => {
    if (isOpen && authToken) {
      if (activeTab === 'incoming') {
        loadIncomingRequests();
      } else if (activeTab === 'outgoing') {
        loadOutgoingRequests();
      }
    }
  }, [activeTab]);

  // Listen to socket events for realtime updates
  useEffect(() => {
    if (!authToken) return;

    const socket = getSocket();
    if (!socket || !socket.connected) return;

    // Handle friend:accepted event - reload both lists
    const handleFriendAccepted = () => {
      console.log('🔄 [FriendsListModal] friend:accepted received, reloading lists...');
      loadIncomingRequests();
      loadOutgoingRequests();
    };

    // Handle friend:cancelled event - reload incoming requests
    const handleFriendCancelled = () => {
      console.log('🔄 [FriendsListModal] friend:cancelled received, reloading incoming requests...');
      loadIncomingRequests();
    };

    // Handle friend:request event - reload incoming requests
    const handleFriendRequest = () => {
      console.log('🔄 [FriendsListModal] friend:request received, reloading incoming requests...');
      loadIncomingRequests();
    };

    socket.on('friend:accepted', handleFriendAccepted);
    socket.on('friend:cancelled', handleFriendCancelled);
    socket.on('friend:request', handleFriendRequest);

    return () => {
      socket.off('friend:accepted', handleFriendAccepted);
      socket.off('friend:cancelled', handleFriendCancelled);
      socket.off('friend:request', handleFriendRequest);
    };
  }, [authToken]);

  // Listen to custom event for real-time outgoing request removal
  useEffect(() => {
    const handleFriendRequestAccepted = (event) => {
      const { request_id, friend_email } = event.detail || {};
      if (!request_id) return;
      
      console.log('🔄 [FriendsListModal] friend-request-accepted event received, removing request:', request_id);
      
      // CRITICAL: Real-time state update - Remove accepted request from outgoing requests immediately
      setOutgoingRequests((prev) => {
        const updated = prev.filter((req) => req.id !== request_id);
        if (updated.length !== prev.length) {
          console.log('✅ [Sync] Removed accepted request from outgoing requests:', request_id);
        }
        return updated;
      });
      
      // Also reload to ensure consistency
      loadOutgoingRequests();
    };

    window.addEventListener('notesplus-friend-request-accepted', handleFriendRequestAccepted);
    
    return () => {
      window.removeEventListener('notesplus-friend-request-accepted', handleFriendRequestAccepted);
    };
  }, []);

  // Accept request
  const handleAccept = async (requestId) => {
    if (!authToken || processingRequest) return;
    setProcessingRequest(requestId);
    try {
      const response = await chatApi.acceptFriendRequest({ token: authToken, requestId });
      if (response.ok) {
        // Reload incoming requests and friends
        await loadIncomingRequests();
        // CRITICAL FIX: Refresh friends list via ChatContext with skipDebounce=true
        // This ensures friends list updates immediately without waiting for debounce
        if (reloadFriends) {
          await reloadFriends(true); // Skip debounce for immediate update
        }
        // Update pending friend request count
        if (loadPendingFriendRequestCount) {
          loadPendingFriendRequestCount(true); // Skip debounce for immediate update
        }
      }
    } catch (error) {
      console.error('Accept request error:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  // Reject request
  const handleReject = async (requestId) => {
    if (!authToken || processingRequest) return;
    setProcessingRequest(requestId);
    try {
      const response = await chatApi.rejectFriendRequest({ token: authToken, requestId });
      if (response.ok) {
        await loadIncomingRequests();
        // Update pending friend request count
        if (loadPendingFriendRequestCount) {
          loadPendingFriendRequestCount(true);
        }
      }
    } catch (error) {
      console.error('Reject request error:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  // Cancel request (for outgoing requests)
  const handleCancel = async (requestId) => {
    if (!authToken || processingRequest) return;
    setProcessingRequest(requestId);
    try {
      const response = await chatApi.cancelFriendRequest({ token: authToken, requestId });
      if (response.ok) {
        await loadOutgoingRequests();
        // Update pending friend request count
        if (loadPendingFriendRequestCount) {
          loadPendingFriendRequestCount();
        }
      }
    } catch (error) {
      console.error('Cancel request error:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  // Filter out deleted friends from the list
  const activeFriends = friends.filter(friend => friend.isDeleted !== true);
  
  const tabs = [
    { id: 'delete', label: t('friendsListModal.tabs.delete'), icon: UserX, count: activeFriends.length },
    { id: 'incoming', label: t('friendsListModal.tabs.incoming'), icon: Mail, count: incomingRequests.length },
    { id: 'outgoing', label: t('friendsListModal.tabs.outgoing'), icon: Send, count: outgoingRequests.length },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-[500px] h-[600px] border border-white/10 dark:border-white/[0.07] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/[0.07]">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#2563eb]/10 dark:bg-[#2563eb]/10 rounded-xl">
                    <Users className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                  </div>
                  <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">{t('friendsListModal.title')}</h2>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] dark:hover:text-[var(--color-text-secondary)] transition-colors"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-white/10 dark:border-white/[0.07] px-4">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <motion.button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${
                        isActive
                          ? 'text-[#3d8ef0] dark:text-[#7ab8f8]'
                          : 'text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      {tab.count > 0 && (
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                          isActive
                            ? 'bg-[#2563eb]/10 dark:bg-[#2563eb]/10 text-[#3d8ef0] dark:text-[#7ab8f8]'
                            : 'bg-[var(--color-bg-elevated)] dark:bg-[#161f31] text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)]'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2563eb] dark:bg-primary-400"
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {activeTab === 'delete' && (
                  <>
                    {activeFriends.length === 0 ? (
                      <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] text-sm py-12">
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-12 h-12 rounded-full bg-[var(--color-bg-elevated)] dark:bg-[#161f31] flex items-center justify-center">
                            <UserX className="w-6 h-6 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                          </div>
                          <p>{t('friendsListModal.noFriendsToDelete')}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {activeFriends.map((friend, index) => (
                          <motion.div
                            key={friend.email || friend.id || `friend-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="p-3.5 rounded-xl border border-white/10 dark:border-white/10/30 bg-[var(--color-bg-base)] dark:bg-[#161f31]/60"
                          >
                            <div className="flex items-start gap-3">
                              {/* Avatar */}
                              <div className="relative flex-shrink-0">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold bg-[#2563eb]/10 dark:bg-[#2563eb]/10 text-[#3d8ef0] dark:text-[#7ab8f8]">
                                  {friend.avatarUrl ? (
                                    <img
                                      src={friend.avatarUrl}
                                      alt={friend.email}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    getInitials(friend.name || friend.email)
                                  )}
                                </div>
                                {/* Status Indicator */}
                                <div
                                  className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-white/10 ${
                                    friend.status === 'online' ? 'bg-green-500' : 'bg-gray-400 dark:bg-[#24354f]'
                                  }`}
                                />
                              </div>

                              {/* Friend Info */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm mb-1">
                                  <Username user={friend} variant="default" />
                                </h3>
                                <p className={`text-xs mb-3 ${getPlusEmailColor(friend)}`}>
                                  {friend.email}
                                </p>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={async () => {
                                    if (window.confirm(t('friendsListModal.deleteFriendConfirm', { name: friend.name || friend.email }))) {
                                      if (deleteFriend) {
                                        await deleteFriend(friend.id || friend.email);
                                        if (reloadFriends) {
                                          await reloadFriends();
                                        }
                                      }
                                    }
                                  }}
                                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                                >
                                  <UserX className="w-4 h-4" />
                                  <span>{t('friendInfoSidebar.deleteFriend')}</span>
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'incoming' && (
                  <>
                    {isLoadingIncoming ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[#3d8ef0] dark:text-[#7ab8f8]" />
                      </div>
                    ) : incomingRequests.length === 0 ? (
                      <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] text-sm py-12">
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-12 h-12 rounded-full bg-[var(--color-bg-elevated)] dark:bg-[#161f31] flex items-center justify-center">
                            <Mail className="w-6 h-6 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                          </div>
                          <p>{t('friendsListModal.noIncoming')}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {incomingRequests.map((request, index) => (
                          <motion.div
                            key={request.id || `incoming-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="p-3.5 rounded-xl border border-white/10 dark:border-white/10/30 bg-[var(--color-bg-base)] dark:bg-[#161f31]/60"
                          >
                            <div className="flex items-start gap-3">
                              {/* Avatar */}
                              <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold bg-[#2563eb]/10 dark:bg-[#2563eb]/10 text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                                {getInitials(request.senderName || request.senderEmail)}
                              </div>

                              {/* Request Info */}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm text-[var(--color-text-primary)] dark:text-white mb-1">
                                  {request.senderName || request.senderEmail}
                                </h3>
                                <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mb-3">
                                  {request.senderEmail}
                                </p>
                                <div className="flex items-center gap-2">
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleAccept(request.id)}
                                    disabled={processingRequest === request.id}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {processingRequest === request.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Check className="w-4 h-4" />
                                        <span>{t('friendsListModal.accept')}</span>
                                      </>
                                    )}
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleReject(request.id)}
                                    disabled={processingRequest === request.id}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {processingRequest === request.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <XIcon className="w-4 h-4" />
                                        <span>{t('friendsListModal.reject')}</span>
                                      </>
                                    )}
                                  </motion.button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'outgoing' && (
                  <>
                    {isLoadingOutgoing ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[#3d8ef0] dark:text-[#7ab8f8]" />
                      </div>
                    ) : outgoingRequests.length === 0 ? (
                      <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] text-sm py-12">
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-12 h-12 rounded-full bg-[var(--color-bg-elevated)] dark:bg-[#161f31] flex items-center justify-center">
                            <Send className="w-6 h-6 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                          </div>
                          <p>{t('friendsListModal.noOutgoing')}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {outgoingRequests.map((request, index) => (
                          <motion.div
                            key={request.id || `outgoing-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="p-3.5 rounded-xl border border-white/10 dark:border-white/10/30 bg-[var(--color-bg-base)] dark:bg-[#161f31]/60"
                          >
                            <div className="flex items-start gap-3">
                              {/* Avatar */}
                              <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold bg-[#2563eb]/10 dark:bg-[#2563eb]/10 text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                                {getInitials(request.receiverEmail)}
                              </div>

                              {/* Request Info */}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm text-[var(--color-text-primary)] dark:text-white mb-1">
                                  {request.receiverEmail}
                                </h3>
                                <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mb-3">
                                  {formatMessageTime(request.createdAt, t, dateLocale)}
                                </p>
                                <div className="flex items-center gap-2">
                                  <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 text-xs font-medium rounded-lg">
                                    <Clock className="w-3 h-3" />
                                    <span>{t('friendsListModal.pending')}</span>
                                  </div>
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleCancel(request.id)}
                                    disabled={processingRequest === request.id}
                                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {processingRequest === request.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <XIcon className="w-3 h-3" />
                                        <span>{t('friendsListModal.cancelInvite')}</span>
                                      </>
                                    )}
                                  </motion.button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FriendsListModal;
